import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Button, IconButton, Input, Select } from '@artosera/ui';
import type { EquipeOuverture } from '../../../types.js';
import styles from '../Ouvertures.module.css';
import { Champ, EMAIL_VALIDE, descriptionChamp, type PropsEtape } from './commun.js';

type Membre = EquipeOuverture['membres'][number];
const ROLES: readonly Membre['role'][] = ['direction', 'equipe', 'lecture', 'comptable'];

/** Étape 4 : la direction, les membres, et le moment des invitations. */
export function EtapeEquipe({
  dossier,
  enCours,
  lectureSeule,
  sauver,
  allerA,
}: PropsEtape): JSX.Element {
  const { t } = useTranslation('console');
  const equipe = dossier?.equipe;

  const [nomDirection, setNomDirection] = useState(equipe?.direction?.nom ?? '');
  const [emailDirection, setEmailDirection] = useState(equipe?.direction?.email ?? '');
  const [membres, setMembres] = useState<Membre[]>(equipe?.membres ?? []);
  const [invitations, setInvitations] = useState<EquipeOuverture['invitations']>(
    equipe?.invitations ?? 'maintenant',
  );
  const [erreurs, setErreurs] = useState<Record<string, string>>({});

  const changerMembre = (rang: number, changement: Partial<Membre>): void =>
    setMembres((courants) =>
      courants.map((membre, position) =>
        position === rang ? { ...membre, ...changement } : membre,
      ),
    );

  const surEnvoi = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const prochaines: Record<string, string> = {};
    if (nomDirection.trim() === '') {
      prochaines.nomDirection = t('ouvertures.equipe.erreurs.nomDirection');
    }
    if (!EMAIL_VALIDE.test(emailDirection.trim())) {
      prochaines.emailDirection = t('ouvertures.equipe.erreurs.emailDirection');
    }
    if (membres.some((m) => m.nom.trim() === '' || !EMAIL_VALIDE.test(m.email.trim()))) {
      prochaines.membres = t('ouvertures.equipe.erreurs.membre');
    }
    const adresses = [emailDirection, ...membres.map((m) => m.email)].map((a) =>
      a.trim().toLowerCase(),
    );
    if (adresses.some((adresse, rang) => adresse !== '' && adresses.indexOf(adresse) !== rang)) {
      prochaines.doublon = t('ouvertures.equipe.erreurs.doublon');
    }
    setErreurs(prochaines);
    if (Object.keys(prochaines).length > 0) return;
    void sauver(
      {
        equipe: {
          direction: { nom: nomDirection.trim(), email: emailDirection.trim().toLowerCase() },
          membres: membres.map((m) => ({
            nom: m.nom.trim(),
            email: m.email.trim().toLowerCase(),
            role: m.role,
          })),
          invitations,
        },
      },
      dossier?.identite.estTest ? 6 : 5,
    );
  };

  return (
    <form className={styles.etape} onSubmit={surEnvoi} noValidate>
      <header>
        <h2>{t('ouvertures.equipe.titre')}</h2>
        <p>{t('ouvertures.equipe.texte')}</p>
      </header>

      <fieldset className={styles.groupe} disabled={lectureSeule}>
        <h3>{t('ouvertures.equipe.direction')}</h3>
        <div className={styles.groupeGrille}>
          <Champ id="ouv-dir-nom" label={t('ouvertures.equipe.nom')} erreur={erreurs.nomDirection}>
            <Input
              id="ouv-dir-nom"
              autoComplete="off"
              value={nomDirection}
              onChange={(evenement) => setNomDirection(evenement.target.value)}
              {...descriptionChamp('ouv-dir-nom', { erreur: Boolean(erreurs.nomDirection) })}
            />
          </Champ>
          <Champ
            id="ouv-dir-email"
            label={t('ouvertures.equipe.email')}
            erreur={erreurs.emailDirection}
          >
            <Input
              id="ouv-dir-email"
              type="email"
              autoComplete="off"
              value={emailDirection}
              onChange={(evenement) => setEmailDirection(evenement.target.value)}
              {...descriptionChamp('ouv-dir-email', { erreur: Boolean(erreurs.emailDirection) })}
            />
          </Champ>
        </div>

        <hr className={styles.separateur} />

        <h3>{t('ouvertures.equipe.membres')}</h3>
        {membres.length === 0 ? (
          <p className={styles.groupeTexte}>{t('ouvertures.equipe.aucunMembre')}</p>
        ) : (
          <ul className={styles.liste}>
            {membres.map((membre, rang) => (
              <li key={rang} className={styles.membre} data-membre={rang}>
                <Champ id={`ouv-membre-nom-${rang}`} label={t('ouvertures.equipe.nom')}>
                  <Input
                    id={`ouv-membre-nom-${rang}`}
                    autoComplete="off"
                    value={membre.nom}
                    onChange={(evenement) => changerMembre(rang, { nom: evenement.target.value })}
                  />
                </Champ>
                <Champ id={`ouv-membre-email-${rang}`} label={t('ouvertures.equipe.email')}>
                  <Input
                    id={`ouv-membre-email-${rang}`}
                    type="email"
                    autoComplete="off"
                    value={membre.email}
                    onChange={(evenement) => changerMembre(rang, { email: evenement.target.value })}
                  />
                </Champ>
                <Champ id={`ouv-membre-role-${rang}`} label={t('ouvertures.equipe.role')}>
                  <Select
                    id={`ouv-membre-role-${rang}`}
                    value={membre.role}
                    onValueChange={(valeur) =>
                      changerMembre(rang, { role: valeur as Membre['role'] })
                    }
                    options={ROLES.map((role) => ({
                      value: role,
                      label: t(`ouvertures.equipe.roles.${role}`),
                    }))}
                  />
                </Champ>
                <IconButton
                  aria-label={t('ouvertures.equipe.retirer', { nom: membre.nom || `#${rang + 1}` })}
                  icon={<Trash2 size={18} strokeWidth={1.5} aria-hidden="true" />}
                  onClick={() => setMembres((courants) => courants.filter((_, i) => i !== rang))}
                />
              </li>
            ))}
          </ul>
        )}
        {erreurs.membres ? (
          <p className={styles.choixAlerte} role="alert">
            {erreurs.membres}
          </p>
        ) : null}
        {erreurs.doublon ? (
          <p className={styles.choixAlerte} role="alert">
            {erreurs.doublon}
          </p>
        ) : null}
        <div>
          <Button
            variant="secondary"
            onClick={() =>
              setMembres((courants) => [...courants, { nom: '', email: '', role: 'equipe' }])
            }
          >
            <Plus size={16} strokeWidth={1.5} aria-hidden="true" /> {t('ouvertures.equipe.ajouter')}
          </Button>
        </div>

        <hr className={styles.separateur} />

        <h3 id="ouv-invitations-titre">{t('ouvertures.equipe.invitations')}</h3>
        <div className={styles.options} role="radiogroup" aria-labelledby="ouv-invitations-titre">
          {(['maintenant', 'a_la_bascule'] as const).map((valeur) => (
            <label key={valeur} className={styles.option}>
              <input
                type="radio"
                name="ouv-invitations"
                value={valeur}
                checked={invitations === valeur}
                onChange={() => setInvitations(valeur)}
              />
              {t(`ouvertures.equipe.invitationsOptions.${valeur}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <div className={styles.actionsEtape}>
        <Button variant="ghost" onClick={() => allerA(3)}>
          {t('ouvertures.assistant.precedent')}
        </Button>
        <div>
          <Button type="submit" loading={enCours} disabled={lectureSeule}>
            {enCours
              ? t('ouvertures.assistant.enregistrement')
              : t('ouvertures.assistant.continuer')}
          </Button>
        </div>
      </div>
    </form>
  );
}
